import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import Button from './Button';

interface DaySchedule {
  day: string;
  isOpen: boolean;
  openTime: string;
  closeTime: string;
}

interface OpeningHoursSelectorProps {
  value: string;
  onChange: (value: string) => void;
}

const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];

const OpeningHoursSelector: React.FC<OpeningHoursSelectorProps> = ({ value, onChange }) => {
  const [schedule, setSchedule] = useState<DaySchedule[]>(() => {
    // Parse existing value or initialize with default schedule
    if (value && value.trim()) {
      return parseScheduleString(value);
    }
    return WEEKDAYS.map(day => ({
      day,
      isOpen: true,
      openTime: '09:00',
      closeTime: '18:00'
    }));
  });

  const [useCustomFormat, setUseCustomFormat] = useState(false);
  const [customText, setCustomText] = useState(value);

  // Parse schedule string into structured data
  function parseScheduleString(scheduleStr: string): DaySchedule[] {
    const defaultSchedule = WEEKDAYS.map(day => ({
      day,
      isOpen: false,
      openTime: '09:00',
      closeTime: '18:00'
    }));

    try {
      // Try to parse common formats like "Mon-Fri: 9:00 AM - 6:00 PM"
      const parts = scheduleStr.split(',').map(s => s.trim());
      
      parts.forEach(part => {
        const match = part.match(/([A-Za-z\-]+):\s*(\d{1,2}:\d{2})\s*(?:AM|PM)?\s*-\s*(\d{1,2}:\d{2})\s*(?:AM|PM)?/i);
        
        if (match) {
          const daysPart = match[1];
          const openTime = match[2];
          const closeTime = match[3];

          if (daysPart.includes('-')) {
            // Range like "Mon-Fri"
            const [start, end] = daysPart.split('-').map(d => d.trim());
            const startIdx = WEEKDAYS.findIndex(day => day.toLowerCase().startsWith(start.toLowerCase()));
            const endIdx = WEEKDAYS.findIndex(day => day.toLowerCase().startsWith(end.toLowerCase()));
            
            if (startIdx !== -1 && endIdx !== -1) {
              for (let i = startIdx; i <= endIdx; i++) {
                defaultSchedule[i].isOpen = true;
                defaultSchedule[i].openTime = openTime;
                defaultSchedule[i].closeTime = closeTime;
              }
            }
          } else {
            // Single day
            const dayIdx = WEEKDAYS.findIndex(day => day.toLowerCase().startsWith(daysPart.toLowerCase()));
            if (dayIdx !== -1) {
              defaultSchedule[dayIdx].isOpen = true;
              defaultSchedule[dayIdx].openTime = openTime;
              defaultSchedule[dayIdx].closeTime = closeTime;
            }
          }
        }
      });
    } catch (err) {
      console.error('Failed to parse schedule:', err);
    }

    return defaultSchedule;
  }

  // Convert schedule to string format
  function scheduleToString(schedule: DaySchedule[]): string {
    const openDays = schedule.filter(day => day.isOpen);
    
    if (openDays.length === 0) {
      return 'Closed';
    }

    // Group consecutive days with same hours
    const groups: Array<{ days: string[]; openTime: string; closeTime: string }> = [];
    let currentGroup: { days: string[]; openTime: string; closeTime: string } | null = null;

    openDays.forEach(day => {
      if (
        !currentGroup ||
        currentGroup.openTime !== day.openTime ||
        currentGroup.closeTime !== day.closeTime
      ) {
        if (currentGroup) {
          groups.push(currentGroup);
        }
        currentGroup = {
          days: [day.day],
          openTime: day.openTime,
          closeTime: day.closeTime
        };
      } else {
        currentGroup.days.push(day.day);
      }
    });

    if (currentGroup) {
      groups.push(currentGroup);
    }

    // Format groups
    return groups
      .map(group => {
        const daysStr =
          group.days.length > 2
            ? `${group.days[0].substring(0, 3)}-${group.days[group.days.length - 1].substring(0, 3)}`
            : group.days.map(d => d.substring(0, 3)).join(', ');

        return `${daysStr}: ${group.openTime} - ${group.closeTime}`;
      })
      .join(', ');
  }

  // Update parent when schedule changes
  useEffect(() => {
    if (!useCustomFormat) {
      const scheduleString = scheduleToString(schedule);
      onChange(scheduleString);
    }
  }, [schedule, useCustomFormat]);

  const handleDayToggle = (index: number) => {
    const newSchedule = [...schedule];
    newSchedule[index].isOpen = !newSchedule[index].isOpen;
    setSchedule(newSchedule);
  };

  const handleTimeChange = (index: number, field: 'openTime' | 'closeTime', value: string) => {
    const newSchedule = [...schedule];
    newSchedule[index][field] = value;
    setSchedule(newSchedule);
  };

  const applyToAll = (sourceIndex: number) => {
    const sourceDay = schedule[sourceIndex];
    const newSchedule = schedule.map(day => ({
      ...day,
      openTime: sourceDay.openTime,
      closeTime: sourceDay.closeTime,
      isOpen: day.isOpen // Keep existing open/closed state
    }));
    setSchedule(newSchedule);
  };

  const toggleAll = (isOpen: boolean) => {
    const newSchedule = schedule.map(day => ({ ...day, isOpen }));
    setSchedule(newSchedule);
  };

  if (useCustomFormat) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">Opening Hours (Custom Format)</label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setUseCustomFormat(false);
              setSchedule(parseScheduleString(customText));
            }}
          >
            Switch to Weekday Selector
          </Button>
        </div>
        <textarea
          value={customText}
          onChange={(e) => {
            setCustomText(e.target.value);
            onChange(e.target.value);
          }}
          rows={3}
          placeholder="e.g., Mon-Fri: 9:00 AM - 6:00 PM, Sat-Sun: Closed"
          className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-3">
        <label className="text-sm font-medium flex items-center space-x-2">
          <Clock className="w-4 h-4" />
          <span>Opening Hours</span>
        </label>
        <div className="flex items-center space-x-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => toggleAll(true)}
          >
            Open All
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => toggleAll(false)}
          >
            Close All
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setUseCustomFormat(true);
              setCustomText(scheduleToString(schedule));
            }}
          >
            Custom Format
          </Button>
        </div>
      </div>

      <div className="bg-muted/30 rounded-lg p-4 space-y-3">
        {schedule.map((day, index) => (
          <div key={day.day} className="flex items-center space-x-3">
            {/* Day checkbox */}
            <label className="flex items-center space-x-2 w-32">
              <input
                type="checkbox"
                checked={day.isOpen}
                onChange={() => handleDayToggle(index)}
                className="w-4 h-4 rounded border-border"
              />
              <span className="text-sm font-medium">{day.day}</span>
            </label>

            {/* Time inputs */}
            {day.isOpen ? (
              <>
                <input
                  type="time"
                  value={day.openTime}
                  onChange={(e) => handleTimeChange(index, 'openTime', e.target.value)}
                  className="px-3 py-1.5 border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <span className="text-sm text-muted-foreground">to</span>
                <input
                  type="time"
                  value={day.closeTime}
                  onChange={(e) => handleTimeChange(index, 'closeTime', e.target.value)}
                  className="px-3 py-1.5 border border-border rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => applyToAll(index)}
                  className="text-xs"
                  title="Apply these hours to all open days"
                >
                  Apply to All
                </Button>
              </>
            ) : (
              <span className="text-sm text-muted-foreground italic">Closed</span>
            )}
          </div>
        ))}
      </div>

      <div className="text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2">
        <strong>Preview:</strong> {scheduleToString(schedule)}
      </div>
    </div>
  );
};

export default OpeningHoursSelector;